import { Link } from 'react-router-dom'
import { APP_NAME, PLAN_LIMITS } from '@/lib/config'
import { useEffect } from 'react'

export default function BestWebsiteAnalytics() {
  useEffect(() => {
    document.title = `Best Website Analytics Tools in 2026 - Privacy-First Comparison | ${APP_NAME}`

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Compare the best website analytics tools in 2026. Side-by-side comparison of UserInsight.live, Google Analytics, Plausible, Fathom, Hotjar, and Microsoft Clarity. Find the best privacy-first analytics for your website.')
    }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-icon.png" alt={APP_NAME} className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900">{APP_NAME}</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Sign in
              </Link>
              <Link to="/signup" className="btn-primary">
                Start free trial
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-primary-600 font-medium mb-4">Updated for 2026</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Best Website Analytics Tools
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Compare the top website analytics tools for 2026. From privacy-first solutions to session replay,
            find the best analytics for your website.
          </p>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-primary-50 border-y border-primary-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Quick Answer: What's the Best Website Analytics Tool?
          </h2>
          <p className="text-gray-700 mb-4">
            For most websites in 2026, <strong>{APP_NAME}</strong> is the best choice because it combines:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
            <li><strong>Privacy-first tracking</strong> - GDPR compliant without complex setup</li>
            <li><strong>Session replay included</strong> - See how users interact with your site</li>
            <li><strong>Lightweight script</strong> - Under 10KB, no impact on site speed</li>
            <li><strong>Free tier</strong> - {PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month at no cost</li>
            <li><strong>One-line installation</strong> - Works with any website in 2 minutes</li>
          </ul>
          <Link to="/signup" className="btn-primary py-3 px-8 text-base inline-block">
            Try {APP_NAME} Free
          </Link>
        </div>
      </section>

      {/* Full Comparison Table */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Complete Comparison: 6 Top Analytics Tools
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Side-by-side comparison of the most popular website analytics tools.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-3 text-gray-600 font-medium">Feature</th>
                  <th className="text-center py-4 px-3 text-primary-600 font-semibold bg-primary-50">{APP_NAME}</th>
                  <th className="text-center py-4 px-3 text-gray-600 font-medium">Google Analytics</th>
                  <th className="text-center py-4 px-3 text-gray-600 font-medium">Plausible</th>
                  <th className="text-center py-4 px-3 text-gray-600 font-medium">Fathom</th>
                  <th className="text-center py-4 px-3 text-gray-600 font-medium">Hotjar</th>
                  <th className="text-center py-4 px-3 text-gray-600 font-medium">Microsoft Clarity</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-4 px-3 text-gray-900 font-medium">{row.feature}</td>
                    <td className="py-4 px-3 text-center bg-primary-50">
                      <CellValue value={row.userinsight} highlight />
                    </td>
                    <td className="py-4 px-3 text-center">
                      <CellValue value={row.ga} />
                    </td>
                    <td className="py-4 px-3 text-center">
                      <CellValue value={row.plausible} />
                    </td>
                    <td className="py-4 px-3 text-center">
                      <CellValue value={row.fathom} />
                    </td>
                    <td className="py-4 px-3 text-center">
                      <CellValue value={row.hotjar} />
                    </td>
                    <td className="py-4 px-3 text-center">
                      <CellValue value={row.clarity} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Best For Each Use Case */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Best Analytics Tool By Use Case
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Different websites have different needs. Here's our recommendation for each scenario.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase) => (
              <div key={useCase.title} className="card">
                <div className="text-2xl mb-3">{useCase.emoji}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-primary-600 font-medium mb-2">{useCase.recommendation}</p>
                <p className="text-gray-600 text-sm">{useCase.reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Individual Tool Summaries */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Tool-by-Tool Breakdown
          </h2>
          <div className="space-y-12">
            {toolSummaries.map((tool) => (
              <div key={tool.name} className="border-b border-gray-200 pb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{tool.name}</h3>
                <p className="text-gray-600 mb-4">{tool.description}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-green-700 mb-2">Pros:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {tool.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-red-700 mb-2">Cons:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {tool.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <XIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Try the Best Privacy-First Analytics
          </h2>
          <p className="text-primary-100 mb-8">
            Start free with {PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month. No credit card required.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-lg bg-white text-primary-600 hover:bg-primary-50 transition-colors"
          >
            Start free trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">Home</Link>
              <Link to="/vs-google-analytics" className="text-gray-500 hover:text-gray-700 text-sm">vs Google Analytics</Link>
              <Link to="/for-shopify" className="text-gray-500 hover:text-gray-700 text-sm">Shopify Analytics</Link>
              <Link to="/for-wordpress" className="text-gray-500 hover:text-gray-700 text-sm">WordPress Analytics</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function CellValue({ value, highlight }: { value: boolean | string; highlight?: boolean }) {
  if (typeof value === 'boolean') {
    return value ? (
      <CheckIcon className={`w-5 h-5 mx-auto ${highlight ? 'text-primary-600' : 'text-green-500'}`} />
    ) : (
      <XIcon className="w-5 h-5 text-red-400 mx-auto" />
    )
  }
  return <span className={highlight ? 'text-primary-700 font-medium' : 'text-gray-600'}>{value}</span>
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

const comparisonData = [
  { feature: 'Privacy-first', userinsight: true, ga: false, plausible: true, fathom: true, hotjar: false, clarity: false },
  { feature: 'GDPR compliant out of box', userinsight: true, ga: false, plausible: true, fathom: true, hotjar: false, clarity: false },
  { feature: 'Session replay', userinsight: true, ga: false, plausible: false, fathom: false, hotjar: true, clarity: true },
  { feature: 'Heatmaps', userinsight: false, ga: false, plausible: false, fathom: false, hotjar: true, clarity: true },
  { feature: 'Free tier', userinsight: '5K sessions', ga: 'Limited', plausible: false, fathom: false, hotjar: 'Limited', clarity: 'Unlimited' },
  { feature: 'Script size', userinsight: '<10KB', ga: '45KB+', plausible: '1KB', fathom: '1KB', hotjar: '100KB+', clarity: '30KB' },
  { feature: 'Cookie consent needed', userinsight: 'Usually no', ga: 'Yes', plausible: 'No', fathom: 'No', hotjar: 'Yes', clarity: 'Yes' },
  { feature: 'First-party cookies', userinsight: true, ga: false, plausible: false, fathom: false, hotjar: false, clarity: false },
  { feature: 'Real-time dashboard', userinsight: true, ga: true, plausible: true, fathom: true, hotjar: false, clarity: false },
  { feature: 'Setup complexity', userinsight: 'Easy', ga: 'Complex', plausible: 'Easy', fathom: 'Easy', hotjar: 'Medium', clarity: 'Easy' },
  { feature: 'Owned by', userinsight: 'Independent', ga: 'Google', plausible: 'Independent', fathom: 'Independent', hotjar: 'Contentsquare', clarity: 'Microsoft' },
  { feature: 'Starting price', userinsight: 'Free', ga: 'Free', plausible: '$9/mo', fathom: '$14/mo', hotjar: 'Free', clarity: 'Free' },
]

const useCases = [
  {
    emoji: '🔒',
    title: 'Privacy-First Website',
    recommendation: 'UserInsight.live',
    reason: 'GDPR compliant out of the box, first-party cookies only, no consent banner needed.',
  },
  {
    emoji: '🛒',
    title: 'E-commerce / Shopify',
    recommendation: 'UserInsight.live',
    reason: 'Auto-detects Shopify, session replay shows checkout friction, lightweight script.',
  },
  {
    emoji: '📝',
    title: 'Blog or Content Site',
    recommendation: 'UserInsight.live or Plausible',
    reason: 'Simple metrics, fast script, generous free tiers (UserInsight) or affordable pricing (Plausible).',
  },
  {
    emoji: '🏢',
    title: 'Enterprise',
    recommendation: 'Google Analytics',
    reason: 'Extensive integrations, advanced attribution, if you have dedicated analysts.',
  },
  {
    emoji: '🎯',
    title: 'UX Research',
    recommendation: 'Hotjar or UserInsight.live',
    reason: 'Session replay and heatmaps. UserInsight is privacy-first, Hotjar has more UX features.',
  },
  {
    emoji: '💰',
    title: 'Zero Budget',
    recommendation: 'Microsoft Clarity or UserInsight.live',
    reason: 'Clarity is unlimited free. UserInsight has 5K sessions free with better privacy.',
  },
]

const toolSummaries = [
  {
    name: 'UserInsight.live',
    description: 'Privacy-first analytics with session replay. The best balance of privacy, features, and ease of use.',
    pros: [
      'GDPR compliant without complex setup',
      'Session replay included in free tier',
      'Lightweight script (<10KB)',
      'First-party cookies only',
      'One-line installation',
      'Free tier with 5,000 sessions/month',
    ],
    cons: [
      'Newer platform (less brand recognition)',
      'No heatmaps (yet)',
      'Smaller feature set than enterprise tools',
    ],
  },
  {
    name: 'Google Analytics',
    description: 'The industry standard with extensive features, but complex and privacy-invasive.',
    pros: [
      'Comprehensive feature set',
      'Integrates with Google ecosystem',
      'Industry standard, familiar to analysts',
      'Free for most use cases',
    ],
    cons: [
      'Complex setup and configuration',
      'GDPR compliance requires work',
      'Heavy script (45KB+)',
      'Samples data at scale',
      'Steep learning curve',
    ],
  },
  {
    name: 'Plausible Analytics',
    description: 'Lightweight, privacy-first analytics. Simple and focused.',
    pros: [
      'Extremely lightweight (1KB)',
      'Privacy-first, no cookies',
      'Simple, clean dashboard',
      'Open source option available',
    ],
    cons: [
      'No free tier',
      'No session replay',
      'Limited features compared to alternatives',
      'Starting at $9/month',
    ],
  },
  {
    name: 'Fathom Analytics',
    description: 'Privacy-focused analytics with a polished interface.',
    pros: [
      'Privacy-first design',
      'Clean, intuitive dashboard',
      'No cookies needed',
      'Excellent uptime and support',
    ],
    cons: [
      'No free tier',
      'No session replay',
      'Higher starting price ($14/month)',
      'Limited customization',
    ],
  },
  {
    name: 'Hotjar',
    description: 'Behavior analytics focused on UX research with heatmaps and recordings.',
    pros: [
      'Comprehensive UX tools',
      'Heatmaps and recordings',
      'User feedback widgets',
      'Good for conversion optimization',
    ],
    cons: [
      'Heavy script (100KB+)',
      'Requires cookie consent',
      'Privacy concerns',
      'Can slow down sites',
      'Limited free tier',
    ],
  },
  {
    name: 'Microsoft Clarity',
    description: 'Free behavior analytics from Microsoft with unlimited data.',
    pros: [
      'Completely free',
      'Unlimited data',
      'Session recordings',
      'Heatmaps included',
    ],
    cons: [
      'Owned by Microsoft (data concerns)',
      'Requires cookie consent',
      'No real-time dashboard',
      'Limited analytics features',
    ],
  },
]

const faqs = [
  {
    question: 'What is the best free website analytics tool?',
    answer: 'For privacy and features, UserInsight.live offers the best free tier with 5,000 sessions/month and session replay included. Microsoft Clarity is unlimited but raises privacy concerns. Google Analytics is free but complex and privacy-invasive.',
  },
  {
    question: 'What is the best Google Analytics alternative?',
    answer: 'UserInsight.live is the best Google Analytics alternative for most websites. It\'s privacy-first, includes session replay, has a generous free tier, and takes just 2 minutes to set up. Plausible and Fathom are also good alternatives but lack session replay and free tiers.',
  },
  {
    question: 'What is the best privacy-first analytics tool?',
    answer: 'UserInsight.live, Plausible, and Fathom are all privacy-first. UserInsight.live stands out by offering session replay (usually a privacy concern) while maintaining privacy through first-party cookies and no personal data collection.',
  },
  {
    question: 'Do I need cookie consent for website analytics?',
    answer: 'It depends on the tool. UserInsight.live, Plausible, and Fathom typically don\'t require consent banners because they\'re privacy-first. Google Analytics, Hotjar, and Microsoft Clarity require consent under GDPR.',
  },
  {
    question: 'What analytics tool has the lightest script?',
    answer: 'Plausible and Fathom have the smallest scripts at ~1KB. UserInsight.live is <10KB which is still much lighter than Google Analytics (45KB+) or Hotjar (100KB+).',
  },
  {
    question: 'What is the best analytics tool for Shopify?',
    answer: 'UserInsight.live auto-detects Shopify stores and provides session replay to see checkout friction. It\'s lightweight so it won\'t slow down your store, and the free tier covers most small to medium stores.',
  },
  {
    question: 'What analytics tool includes session replay?',
    answer: 'UserInsight.live, Hotjar, and Microsoft Clarity include session replay. UserInsight.live is the only one that combines session replay with privacy-first tracking (no personal data, first-party cookies only).',
  },
  {
    question: 'Is Google Analytics still worth using in 2026?',
    answer: 'For enterprise teams with dedicated analysts, yes. For most websites, simpler privacy-first alternatives like UserInsight.live provide the metrics you need without the complexity and privacy concerns.',
  },
]
